import { ChevronDownIcon, CirclePlusIcon } from '@/icons';
import { Button, DatePicker, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import {
  useGetAllDepartmentsQuery,
  useGetDailyStatesQuery,
  useGetEmployeeStatsQuery,
  useGetLastActionStatsQuery,
} from '../../../../app/features/employee/emp-dashboard/emp-dashboard.api';
import useAuth from '../../../../hooks/useAuth';
import { useAppDiscloser } from '@/hooks';
import DateDownIcon from './_components/Summery/_icons/DateDownIcon';
import AttendanceTrend from './_components/Summery/AttendanceTrend';
import BreakNotificationsCard from './_components/Summery/BreakNotificationsCard';
import DashboardAttendanceCard from './_components/Summery/DashboardAttendanceCard';
import QuickActionsCard from './_components/Summery/QuickActionsCard';

const Summery = () => {
  const { organizationId } = useAuth();
  const [, openCreateEmployee] = useAppDiscloser('CREATE_EMPLOYEE');
  // const [dates, setDates] = useState<[Dayjs | null, Dayjs | null]>([
  //   dayjs().startOf("month"),
  //   dayjs().endOf("month"),
  // ]);
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().subtract(29, 'day'), // 29 days ago + today = 30 days total
    dayjs(), // Today as end date
  ]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const { RangePicker } = DatePicker;

  // Format dates for API parameters
  const start_date = dates[0]?.format('YYYY-MM-DD');
  const end_date = dates[1]?.format('YYYY-MM-DD');

  // Department API call
  const {
    data: departmentsData,
    isLoading: isDepartmentsLoading,
    isFetching: isDepartmentsFetching,
  } = useGetAllDepartmentsQuery({
    params: { page: 1, limit: 100 },
  });

  // API calls for dashboard data
  const {
    data: attendanceTrendData,
    isLoading: isAttendanceTrendLoading,
    isFetching: isAttendanceTrendFetching,
  } = useGetDailyStatesQuery(
    {
      start_date,
      end_date,
      organization_id: organizationId,
      department_id: selectedDepartment || undefined,
    },
    { skip: !organizationId },
  );
  const {
    data: employeeStatsData,
    isLoading: isEmployeeStatsLoading,
    isFetching: isEmployeeStatsFetching,
  } = useGetEmployeeStatsQuery(
    {
      start_date,
      end_date,
      organization_id: organizationId,
      department_id: selectedDepartment || undefined,
    },
    { skip: !organizationId },
  );
  const { data: lastActionStatsData, isLoading: isLastActionStatsLoading } =
    useGetLastActionStatsQuery(
      {
        organizationId: organizationId,
      },
      { skip: !organizationId },
    );

  return (
    <div>
      {/* Filter */}
      <div className="dark:bg-theme-slate-800 border dark:border-theme-gray-700 p-3 rounded-xl w-full max-w-full mx-auto">
        <div className="flex flex-wrap justify-center xl:justify-between items-center gap-3">
          <div className="relative inline-flex items-center  text-white  cursor-pointer select-none">
            {/* TEXT */}
            <span className="text-white text-xl lg:text-2xl xl:text-3xl font-bold">
              {dates[0]?.format('MMMM DD')} - {dates[1]?.format('MMMM DD')}
            </span>

            <DateDownIcon className="ml-2 text-white text-xl lg:text-2xl xl:text-3xl" />

            {/* HIDDEN RANGE PICKER */}
            <RangePicker
              value={dates}
              onChange={(val) => setDates(val as [Dayjs | null, Dayjs | null])}
              popupClassName="custom-dark-picker"
              className="absolute inset-0 opacity-0 cursor-pointer"
              allowClear={false}
              disabledDate={(current) => {
                return current && current > dayjs().endOf('day');
              }}
              showToday={false}
            />
          </div>

          <div className="flex gap-3 justify-end filter-section">
            <Select
              size="large"
              placeholder="Department"
              className="min-w-36 dashboard-select"
              suffixIcon={<ChevronDownIcon size={24} />}
              // value={selectedDepartment}
              onChange={setSelectedDepartment}
              loading={isDepartmentsLoading || isDepartmentsFetching}
              allowClear
              placement="bottomRight"
              showSearch
              popupMatchSelectWidth={200}
            >
              {/* <Select.Option value="">Department</Select.Option> */}
              {departmentsData?.data?.data?.map((dept) => (
                <Select.Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Select.Option>
              ))}
            </Select>
            <Button
              size="large"
              className="btn-primary"
              onClick={() => openCreateEmployee({ type: 'ADD' })}
            >
              <CirclePlusIcon size={20} />
              Add New Employee
            </Button>
            {/* <Select
              size="large"
              className="w-36"
              placeholder="All Branches"
              suffixIcon={<ChevronDownIcon size={24} />}
            /> */}

            {/* <Button
              disabled
              size="large"
              className="btn-primary !text-neutral-700 !text-base disabled:hover:!text-neutral-700 disabled:hover:opacity-80 font-bold"
            >
              Export
              <ExportDownloadIcon size={16} />
            </Button> */}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6 mt-6">
        {/* RIGHT SIDE - Show first on mobile */}
        <div className="col-span-12 md:col-span-12 lg:col-span-12 xl:col-span-4 order-1  xl:order-2">
          <DashboardAttendanceCard
            employeeStats={employeeStatsData?.data?.data}
            isLoading={isEmployeeStatsLoading || isEmployeeStatsFetching}
          />
        </div>

        {/* LEFT SIDE - Show second on mobile */}
        <div className="col-span-12 md:col-span-12 lg:col-span-12 xl:col-span-8 order-2  xl:order-1">
          <AttendanceTrend
            attendanceTrendData={attendanceTrendData?.data}
            isLoading={isAttendanceTrendLoading || isAttendanceTrendFetching}
          />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6 mt-6">
        {/* LEFT SIDE (QuickActionsCard) */}
        <div className="col-span-12 md:col-span-12 lg:col-span-12 xl:col-span-8">
          <QuickActionsCard />
        </div>

        {/* RIGHT SIDE (BreakNotificationsCard) */}
        <div className="col-span-12 md:col-span-12 lg:col-span-12 xl:col-span-4">
          <BreakNotificationsCard
            lastActionStats={lastActionStatsData?.data?.data}
            isLoading={isLastActionStatsLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default Summery;
